import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@startupquickstart/frontend';
import { useState } from 'react';
import { useGenerateThemeTranslationVoiceovers } from '@/hooks/useGenerateThemeTranslationVoiceovers';

interface GenerateThemeTranslationVoiceoversDialogProps {
  attractionId: string;
  themeId: string;
  translationId: string;
  open: boolean;
  onClose: () => void;
}

const VOICE_OPTIONS = [
  { value: 'alloy', label: 'Alloy (Neutral)' },
  { value: 'echo', label: 'Echo (Masculine)' },
  { value: 'fable', label: 'Fable (British)' },
  { value: 'onyx', label: 'Onyx (Deep)' },
  { value: 'nova', label: 'Nova (Feminine)' },
  { value: 'shimmer', label: 'Shimmer (Bright)' },
] as const;

export function GenerateThemeTranslationVoiceoversDialog({
  attractionId,
  themeId,
  translationId,
  open,
  onClose,
}: GenerateThemeTranslationVoiceoversDialogProps) {
  const [voiceoverType, setVoiceoverType] = useState<
    'both' | 'description' | 'long_description'
  >('both');
  const [voice, setVoice] = useState<
    'alloy' | 'echo' | 'fable' | 'onyx' | 'nova' | 'shimmer'
  >('alloy');

  const generateMutation = useGenerateThemeTranslationVoiceovers();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    generateMutation.mutate(
      { attractionId, themeId, translationId, voiceoverType, voice },
      {
        onSuccess: () => {
          onClose();
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Generate Translation Voiceovers</DialogTitle>
            <DialogDescription>
              Generate AI-powered voiceovers from the translated theme descriptions using AI text-to-speech technology.
            </DialogDescription>
          </DialogHeader>

          <div className="py-4 space-y-4">
            <div>
              <label htmlFor="voiceoverType" className="block text-sm font-medium mb-2">
                Voiceover Type <span className="text-destructive">*</span>
              </label>
              <select
                id="voiceoverType"
                required
                className="w-full px-3 py-2 border rounded-md"
                value={voiceoverType}
                onChange={(e) =>
                  setVoiceoverType(
                    e.target.value as 'both' | 'description' | 'long_description',
                  )
                }
                disabled={generateMutation.isPending}
              >
                <option value="both">Both (Short + Long)</option>
                <option value="description">Short Description Only</option>
                <option value="long_description">Long Description Only</option>
              </select>
              <p className="text-xs text-muted-foreground mt-1">
                Choose which voiceovers to generate
              </p>
            </div>

            <div>
              <label htmlFor="voice" className="block text-sm font-medium mb-2">
                AI Voice <span className="text-destructive">*</span>
              </label>
              <select
                id="voice"
                required
                className="w-full px-3 py-2 border rounded-md"
                value={voice}
                onChange={(e) =>
                  setVoice(
                    e.target.value as 'alloy' | 'echo' | 'fable' | 'onyx' | 'nova' | 'shimmer',
                  )
                }
                disabled={generateMutation.isPending}
              >
                {VOICE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <p className="text-xs text-muted-foreground mt-1">
                Select the AI voice for text-to-speech conversion
              </p>
            </div>
          </div>

          {generateMutation.isPending && (
            <div className="py-2 text-sm text-muted-foreground">
              🎙️ Generating voiceovers... This may take 30-60 seconds.
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={generateMutation.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" loading={generateMutation.isPending}>
              Generate Voiceovers
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
